// ─── WorkflowBar — componente shared cross-artefacto (ADR-025 + 0628) ─────────
// Renderiza la barra de acciones del workflow unificado. Trabaja sobre cualquier
// artefactoTipo soportado por el backend (ORGANIGRAMA, DOC, FLUJOGRAMA y futuros).
// El padre indica el `estadoActual` y el endpoint a llamar; este componente
// resuelve qué botones mostrar según las transiciones legales y dispara la
// llamada con confirmación + manejo de errores.

import { useState } from 'react';
import { useI18n } from '../../../i18n';
import { request } from '../services/api';
import { ConfirmModal, type ConfirmVariant } from './ConfirmModal';

// =============================================================================
// Tipos públicos
// =============================================================================

export type EstadoWorkflow =
  | 'BORRADOR'
  | 'EN_REVISION'
  | 'APROBADO'
  | 'PUBLICADO'
  | 'RECHAZADO'
  | 'DESCARTADO'
  | 'ARCHIVADO';

export type AccionWorkflow =
  | 'ENVIAR_REVISION'
  | 'APROBAR'
  | 'RECHAZAR'
  | 'PUBLICAR'
  | 'DESCARTAR'
  | 'ARCHIVAR'
  | 'REABRIR';

export type ArtefactoTipo = 'ORGANIGRAMA' | 'DOC' | 'FLUJOGRAMA' | 'PROCESO';

export interface WorkflowBarProps {
  /** Tipo de artefacto para el motor unificado. */
  artefactoTipo: ArtefactoTipo;
  /** Identificador del artefacto (versionId / flowchartId / etc.). */
  artefactoId: string;
  /** Estado actual del artefacto (gobierna qué botones se muestran). */
  estadoActual: EstadoWorkflow;
  /** Path absoluto (sin BASE) del endpoint /transitar. Ej: '/organigrama/versiones/abc/transitar'. */
  endpointTransitar: string;
  /** Subset de acciones a ofrecer; si se omite, se calculan por máquina de estados. */
  accionesPermitidas?: AccionWorkflow[];
  /** Callback tras transición OK con el body del backend. */
  onTransicionOk?: (data: unknown) => void;
  /** Callback tras error (mensaje legible para mostrar). */
  onTransicionError?: (msg: string) => void;
  /** Si true, no muestra acciones (solo lectura). */
  readOnly?: boolean;
  /** Si true, renderiza compacto (sin labels textuales, solo iconos). */
  compact?: boolean;
}

// =============================================================================
// Tabla de transiciones legales (espejo del workflow-engine backend)
// =============================================================================

const TRANSICIONES: Record<EstadoWorkflow, AccionWorkflow[]> = {
  BORRADOR: ['ENVIAR_REVISION', 'DESCARTAR'],
  EN_REVISION: ['APROBAR', 'RECHAZAR'],
  APROBADO: ['PUBLICAR'],
  PUBLICADO: ['ARCHIVAR'],
  RECHAZADO: ['REABRIR'],
  DESCARTADO: [],
  ARCHIVADO: [],
};

// =============================================================================
// Estilos por acción (color + icono SVG inline)
// =============================================================================

interface AccionStyle {
  bg: string;
  color: string;
  iconPath: string;
  requireMotivo?: boolean;
}

const ACCION_STYLES: Record<AccionWorkflow, AccionStyle> = {
  ENVIAR_REVISION: {
    bg: '#2F5496',
    color: '#fff',
    iconPath: 'M3 10l9-7 9 7v11H3z M9 21V12h6v9',
  },
  APROBAR: {
    bg: '#16A34A',
    color: '#fff',
    iconPath: 'M20 6L9 17l-5-5',
  },
  RECHAZAR: {
    bg: '#DC2626',
    color: '#fff',
    iconPath: 'M18 6L6 18 M6 6l12 12',
    requireMotivo: true,
  },
  PUBLICAR: {
    bg: '#33BCE1',
    color: '#fff',
    iconPath: 'M3 11l19-9-9 19-2-8-8-2z',
  },
  DESCARTAR: {
    bg: '#fff',
    color: '#DC2626',
    iconPath:
      'M3 6h18 M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2 M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6',
    requireMotivo: false,
  },
  ARCHIVAR: {
    bg: '#fff',
    color: '#6B7280',
    iconPath: 'M21 8v13H3V8 M1 3h22v5H1z M10 12h4',
  },
  REABRIR: {
    bg: '#fff',
    color: '#2F5496',
    iconPath: 'M1 4v6h6 M3.51 15a9 9 0 1 0 2.13-9.36L1 10',
  },
};

// =============================================================================
// Componente
// =============================================================================

// 0651: hints descriptivos al hover migrados a i18n (`t.workflow.hints.*`).
// La función `hintDe(a, t)` se define dentro del componente para acceder al hook.

// Variante visual del modal de confirmación según la acción.
const ACCION_VARIANT: Record<AccionWorkflow, ConfirmVariant> = {
  ENVIAR_REVISION: 'primary',
  APROBAR: 'success',
  RECHAZAR: 'danger',
  PUBLICAR: 'primary',
  DESCARTAR: 'danger',
  ARCHIVAR: 'primary',
  REABRIR: 'primary',
};

export function WorkflowBar({
  artefactoTipo: _artefactoTipo,
  artefactoId: _artefactoId,
  estadoActual,
  endpointTransitar,
  accionesPermitidas,
  onTransicionOk,
  onTransicionError,
  readOnly,
  compact,
}: WorkflowBarProps) {
  const { t } = useI18n();
  const [loading, setLoading] = useState<AccionWorkflow | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // 0615+: estado del modal de confirmación. Reemplaza window.confirm/prompt.
  const [pendingAction, setPendingAction] = useState<AccionWorkflow | null>(null);

  if (readOnly) return null;

  const acciones = accionesPermitidas ?? TRANSICIONES[estadoActual] ?? [];
  if (acciones.length === 0) return null;

  const labelDe = (a: AccionWorkflow): string => {
    const map: Record<AccionWorkflow, string> = {
      ENVIAR_REVISION: t.workflow.sendToReview,
      APROBAR: t.workflow.approve,
      RECHAZAR: t.workflow.reject,
      PUBLICAR: t.workflow.publish,
      DESCARTAR: t.workflow.discard,
      ARCHIVAR: t.workflow.archive,
      REABRIR: t.workflow.reopen,
    };
    return map[a];
  };

  // 0651: hints descriptivos i18n cross-artefacto.
  const hintDe = (a: AccionWorkflow): string => {
    const map: Record<AccionWorkflow, string> = {
      ENVIAR_REVISION: t.workflow.hints.enviarRevision,
      APROBAR: t.workflow.hints.aprobar,
      RECHAZAR: t.workflow.hints.rechazar,
      PUBLICAR: t.workflow.hints.publicar,
      DESCARTAR: t.workflow.hints.descartar,
      ARCHIVAR: t.workflow.hints.archivar,
      REABRIR: t.workflow.hints.reabrir,
    };
    return map[a];
  };

  async function ejecutarAccion(
    accion: AccionWorkflow,
    payload?: { motivoRechazo?: string; asunto?: string; alcance?: string },
  ) {
    setLoading(accion);
    setErrorMsg(null);
    try {
      const data = await request(endpointTransitar, {
        method: 'POST',
        body: JSON.stringify({ accion, ...payload }),
      });
      onTransicionOk?.(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(msg);
      onTransicionError?.(msg);
    } finally {
      setLoading(null);
      setPendingAction(null);
    }
  }

  function handleClick(accion: AccionWorkflow) {
    // 0615+: TODAS las acciones piden confirmación (modal centrado).
    setErrorMsg(null);
    setPendingAction(accion);
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        fontFamily: 'inherit',
      }}
    >
      {/* Estado actual badge */}
      <span
        style={{
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          background: '#ECEEF2',
          color: '#1F3864',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
        aria-label="estado-actual"
      >
        {estadoActual.replace('_', ' ')}
      </span>

      {/* Botones de acción */}
      {acciones.map((a) => {
        const style = ACCION_STYLES[a];
        const isLoading = loading === a;
        const isOutline = style.bg === '#fff';
        return (
          <button
            key={a}
            type="button"
            onClick={() => handleClick(a)}
            disabled={loading !== null}
            aria-label={labelDe(a)}
            title={`${labelDe(a)} — ${hintDe(a)}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: compact ? '6px 8px' : '6px 12px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: loading !== null ? 'not-allowed' : 'pointer',
              opacity: loading !== null && !isLoading ? 0.5 : 1,
              background: style.bg,
              color: style.color,
              border: isOutline ? `1px solid ${style.color}` : 'none',
              transition: 'opacity 0.15s, transform 0.05s',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke={style.color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={style.iconPath} />
            </svg>
            {!compact && <span>{isLoading ? '…' : labelDe(a)}</span>}
          </button>
        );
      })}

      {/* Error inline */}
      {errorMsg && (
        <span
          style={{
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 11,
            background: '#FEF2F2',
            color: '#DC2626',
            border: '1px solid #FECACA',
          }}
          role="alert"
        >
          {errorMsg}
        </span>
      )}

      {/* 0615+: modal de confirmación centrado para todas las acciones.
          0665: ENVIAR_REVISION exige asunto+alcance cross-artefacto. */}
      <ConfirmModal
        open={pendingAction !== null}
        title={pendingAction ? labelDe(pendingAction) : ''}
        message={pendingAction ? hintDe(pendingAction) : ''}
        confirmLabel={pendingAction ? labelDe(pendingAction) : ''}
        variant={pendingAction ? ACCION_VARIANT[pendingAction] : 'primary'}
        requireMotivo={
          pendingAction === 'ENVIAR_REVISION'
            ? false
            : pendingAction
              ? ACCION_STYLES[pendingAction].requireMotivo
              : false
        }
        motivoLabel={t.workflow.motivoRechazoPrompt}
        requireAsuntoAlcance={pendingAction === 'ENVIAR_REVISION'}
        loading={loading !== null}
        onConfirm={(motivo) => {
          if (pendingAction) ejecutarAccion(pendingAction, { motivoRechazo: motivo });
        }}
        onConfirmAsuntoAlcance={({ asunto, alcance }) => {
          if (pendingAction) ejecutarAccion(pendingAction, { asunto, alcance });
        }}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}
